import { useSelector } from 'dva';

export const COMMERCIAL_BILLING_SECTION_KEY = 'commercial-and-billing';

export const COMMERCIAL_BILLING_NAV_KEYS = Object.freeze({
  ONBOARDING: 'onboarding',
  SUBSCRIPTIONS: 'subscriptions',
  OPERATORS: 'operators',
  DISTRIBUTORS: 'distributors',
  PARTNERS: 'partners',
  RESELLERS: 'resellers',
  REFERRAL_AGENTS: 'referral-agents',
  CUSTOMERS: 'customers',
  CUSTOMER_ASSIGNMENTS: 'customer-assignments',
  REGISTRATION_APPROVALS: 'registration-approvals',
  SUBSCRIPTION_PAYMENTS: 'subscription-payments',
  COMMERCIAL_AGREEMENTS: 'commercial-agreements',
  PLANS: 'plans',
  FINANCIAL_ADMINISTRATION: 'financial-administration',
  CUSTOMER_DISCOUNTS: 'customer-discounts',
});

/** Wire value of `GET /me/navigation.commercialScope` for hub Commercial readers. */
export const COMMERCIAL_ACCESS_SCOPE_GLOBAL = 'global';

const NAV_ITEM_KEY_TO_FRONTEND_ROUTE = Object.freeze({
  [COMMERCIAL_BILLING_NAV_KEYS.ONBOARDING]: '/configuration/channel-onboarding',
  [COMMERCIAL_BILLING_NAV_KEYS.SUBSCRIPTIONS]: '/configuration/billing-subscriptions',
  [COMMERCIAL_BILLING_NAV_KEYS.OPERATORS]: '/configuration/channel-operators',
  [COMMERCIAL_BILLING_NAV_KEYS.DISTRIBUTORS]: '/configuration/channel-distributors',
  [COMMERCIAL_BILLING_NAV_KEYS.PARTNERS]: '/configuration/channel-partners',
  [COMMERCIAL_BILLING_NAV_KEYS.RESELLERS]: '/configuration/channel-resellers',
  [COMMERCIAL_BILLING_NAV_KEYS.REFERRAL_AGENTS]: '/configuration/channel-referral-agents',
  [COMMERCIAL_BILLING_NAV_KEYS.CUSTOMERS]: '/configuration/channel-customers',
  [COMMERCIAL_BILLING_NAV_KEYS.REGISTRATION_APPROVALS]:
    '/configuration/channel-registration-approvals',
  [COMMERCIAL_BILLING_NAV_KEYS.SUBSCRIPTION_PAYMENTS]:
    '/configuration/billing-subscription-payments',
  [COMMERCIAL_BILLING_NAV_KEYS.COMMERCIAL_AGREEMENTS]: '/configuration/channel-agreements',
  [COMMERCIAL_BILLING_NAV_KEYS.PLANS]: '/configuration/channel-plans',
  [COMMERCIAL_BILLING_NAV_KEYS.FINANCIAL_ADMINISTRATION]: '/configuration/financial-administration',
  [COMMERCIAL_BILLING_NAV_KEYS.CUSTOMER_DISCOUNTS]: '/configuration/customer-discounts',
});

const COMMERCIAL_BILLING_CONFIGURATION_PATHS = new Set([
  ...Object.values(NAV_ITEM_KEY_TO_FRONTEND_ROUTE),
  '/configuration/channel-customer-assignments',
]);

/** These screens are listed by GET /me/navigation. Route roles do not gate them. */
export const isCommercialBillingConfigurationPath = (pathname) =>
  COMMERCIAL_BILLING_CONFIGURATION_PATHS.has(pathname);

/** Frontend route used by the configuration sidebar menu. */
export const resolveFrontendMenuRoute = (item = {}) => {
  if (item.key && NAV_ITEM_KEY_TO_FRONTEND_ROUTE[item.key]) {
    return NAV_ITEM_KEY_TO_FRONTEND_ROUTE[item.key];
  }

  if (item.path?.startsWith('/configuration/')) {
    return item.path;
  }

  return null;
};

/** Backend API base path without leading slash (e.g. billing/company-subscriptions). */
export const normalizeNavigationApiPath = (path) => {
  if (!path || typeof path !== 'string') return null;
  return path.replace(/^\/+/, '');
};

export const getCommercialBillingApiRoute = (sessionNavigation = {}, navigationKey) =>
  sessionNavigation.commercialBillingApiRoutes?.[navigationKey] ?? null;

export const getCommercialBillingApiSubRoute = (apiRoutes = {}, navigationKey, suffix) => {
  const baseRoute = apiRoutes?.[navigationKey];
  if (!baseRoute || !suffix) return null;
  return `${baseRoute}/${suffix}`;
};

export function* selectCommercialBillingApiRoute(navigationKey, select) {
  return getCommercialBillingApiRoute(
    yield select((state) => state.sessionNavigation),
    navigationKey,
  );
}

export const buildCommercialBillingSectionData = (section) => {
  if (!section?.items?.length) {
    return { menuItems: [], apiRoutes: {} };
  }

  const menuItems = [];
  const apiRoutes = {};

  section.items.forEach((item) => {
    if (item.visible === false || !item.key) return;

    const apiPath = normalizeNavigationApiPath(item.path);
    if (!apiPath) return;

    apiRoutes[item.key] = apiPath;

    const frontendRoute = resolveFrontendMenuRoute(item);
    if (!frontendRoute) return;

    menuItems.push({
      key: frontendRoute,
      label: item.label,
      disabled: item.enabled === false,
      disabledReason: item.disabledReason,
    });
  });

  return { menuItems, apiRoutes };
};

const parseNavigationBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === 'True') return true;
  if (value === 'false' || value === 'False') return false;
  return null;
};

/** `GET /me/navigation.isAutomyOwner`. Omitted is not an owner. */
export const parseNavigationIsAutomyOwner = (payload) =>
  parseNavigationBoolean(payload?.isAutomyOwner ?? payload?.IsAutomyOwner);

/** `GET /me/navigation.canCreateCustomer`. Omitted is not allowed. */
export const parseNavigationCanCreateCustomer = (payload) =>
  parseNavigationBoolean(
    payload?.canCreateCustomer ??
      payload?.CanCreateCustomer ??
      payload?.canCreateCustomers ??
      payload?.CanCreateCustomers,
  );

/**
 * `GET /me/navigation.commercialScope`. Omitted is not global.
 * Wire values: `none` | `graph` | `global`.
 */
export const parseNavigationCommercialScope = (payload) => {
  const raw = payload?.commercialScope ?? payload?.CommercialScope;
  if (raw == null || raw === '') return null;
  return String(raw).trim().toLowerCase();
};

export const isCommercialAccessScopeGlobal = (commercialScope) =>
  commercialScope === COMMERCIAL_ACCESS_SCOPE_GLOBAL;

/**
 * Clientes, Solicitudes, Pagos and Financial Administration may be omitted
 * from `sections[].items`. Paint them from the navigation root flags.
 * An item the API already sent keeps that item's `visible` decision.
 */
const NAVIGATION_FLAG_MENU_ITEMS = [
  {
    navigationKey: COMMERCIAL_BILLING_NAV_KEYS.CUSTOMERS,
    labelId: 'global.text.channelCustomers',
    isVisible: ({ isAutomyOwner, canCreateCustomer }) =>
      canCreateCustomer === true || isAutomyOwner === true,
  },
  {
    navigationKey: COMMERCIAL_BILLING_NAV_KEYS.REGISTRATION_APPROVALS,
    labelId: 'global.text.channelRegistrationApprovals',
    apiPath: 'channel/registration-approvals',
    isVisible: ({ isAutomyOwner }) => isAutomyOwner === true,
  },
  {
    navigationKey: COMMERCIAL_BILLING_NAV_KEYS.SUBSCRIPTION_PAYMENTS,
    labelId: 'global.text.billingSubscriptionPayments',
    isVisible: ({ isAutomyOwner }) => isAutomyOwner === true,
  },
  {
    navigationKey: COMMERCIAL_BILLING_NAV_KEYS.FINANCIAL_ADMINISTRATION,
    labelId: 'global.text.financialAdministration',
    apiPath: 'channel/financial-administration',
    isVisible: ({ commercialScope }) => isCommercialAccessScopeGlobal(commercialScope),
  },
];

export const appendNavigationFlagMenuItems = (
  section,
  menuItems = [],
  apiRoutes = {},
  flags = {},
) => {
  const emittedKeys = new Set((section?.items || []).map((item) => item?.key).filter(Boolean));
  const nextMenuItems = [...menuItems];
  const nextApiRoutes = { ...apiRoutes };
  const existingRoutes = new Set(nextMenuItems.map((item) => item.key));

  NAVIGATION_FLAG_MENU_ITEMS.forEach((entry) => {
    if (emittedKeys.has(entry.navigationKey)) return;

    const frontendRoute = NAV_ITEM_KEY_TO_FRONTEND_ROUTE[entry.navigationKey];
    if (!frontendRoute || existingRoutes.has(frontendRoute) || !entry.isVisible(flags)) return;

    nextMenuItems.push({
      key: frontendRoute,
      labelId: entry.labelId,
      disabled: false,
    });
    if (entry.apiPath) nextApiRoutes[entry.navigationKey] = entry.apiPath;
    existingRoutes.add(frontendRoute);
  });

  return { menuItems: nextMenuItems, apiRoutes: nextApiRoutes };
};

export const findCommercialBillingSection = (sections = []) =>
  sections.find((section) => section.key === COMMERCIAL_BILLING_SECTION_KEY);

/** Label from GET /me/navigation for commercial billing routes (matches sidebar menu). */
export const resolveCommercialBillingMenuLabel = (pathname, menuItems = [], formatMessage) => {
  if (!pathname || !menuItems.length) return null;
  const item = menuItems.find((entry) => entry.key === pathname);
  if (!item) return null;
  if (item.label) return item.label;
  if (item.labelId && formatMessage) return formatMessage({ id: item.labelId });
  return null;
};

export const useCommercialBillingApiRoute = (navigationKey) =>
  useSelector((state) => getCommercialBillingApiRoute(state.sessionNavigation, navigationKey));
